/**
 * Classifieur ask v0 — tokens FR dédiés, ordre fixe (premier match gagne).
 * Ne pas réutiliser findMatchingProfessionsSimple (fallback 3 métiers).
 */

export type AskIntent =
  | 'sante'
  | 'pension'
  | 'garde'
  | 'educ'
  | 'comportement'
  | 'promenade'
  | 'generic';

export type ClassifiedAsk = {
  intent: AskIntent;
  /** Slug API GET /search/pros — veterinarian pour intent santé (discovery Places). */
  professionApiSlug: string | null;
  /** Clé METIERS_DATA (SEO), pas le slug API. */
  seoMetierSlug: string | null;
};

const HEALTH_TOKENS = [
  'urgence',
  'veto',
  'veterinaire',
  'fievre',
  'blessure',
  'medicament',
  'medicaments',
  'tiques',
  'tique',
  'osteo',
  'osteopathe',
  'mal au dos',
];

const PENSION_TOKENS = ['pension', 'box', 'dortoir', 'premiere nuit'];

const GARDE_TOKENS = [
  'vacances',
  'pars',
  'partir',
  'cles',
  'cle',
  'sitter',
  'petsitter',
  'pet sitter',
  'seul',
  'seule',
  'solitude',
  'bureau',
  'absence',
  'absences',
  'garde',
];

const EDUC_TOKENS = [
  'tire',
  'laisse',
  'rappel',
  'mordille',
  'obeissance',
  'chiot',
  'educateur',
  'educatrice',
];

const COMPORTEMENT_TOKENS = ['peur', 'reactif', 'reactive', 'anxieux', 'anxieuse', 'artifice', 'feux', 'feu'];

const PROMENADE_TOKENS = ['promeneur', 'promeneuse', 'balade', 'canicule', 'promenade'];

export function normalizeAskText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’]/g, ' ')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function hasToken(normalized: string, tokens: readonly string[]): boolean {
  const padded = ` ${normalized} `;
  return tokens.some(token => {
    if (token.includes(' ')) return normalized.includes(token);
    return padded.includes(` ${token} `);
  });
}

export function classifyAskIntent(phrase: string): ClassifiedAsk {
  const normalized = normalizeAskText(phrase);

  if (!normalized) {
    return { intent: 'generic', professionApiSlug: 'trainers', seoMetierSlug: 'educateur-canin' };
  }

  if (hasToken(normalized, HEALTH_TOKENS)) {
    return {
      intent: 'sante',
      professionApiSlug: 'veterinarian',
      seoMetierSlug: null,
    };
  }

  if (hasToken(normalized, PENSION_TOKENS)) {
    return { intent: 'pension', professionApiSlug: 'pension', seoMetierSlug: 'pension-canine' };
  }

  if (hasToken(normalized, GARDE_TOKENS)) {
    return { intent: 'garde', professionApiSlug: 'pet-sitter', seoMetierSlug: 'pet-sitter' };
  }

  if (hasToken(normalized, EDUC_TOKENS)) {
    return { intent: 'educ', professionApiSlug: 'trainers', seoMetierSlug: 'educateur-canin' };
  }

  if (hasToken(normalized, COMPORTEMENT_TOKENS) || hasToken(normalized, ['detruit', 'destruction'])) {
    return {
      intent: 'comportement',
      professionApiSlug: 'behaviorist',
      seoMetierSlug: 'comportementaliste-canin',
    };
  }

  if (hasToken(normalized, PROMENADE_TOKENS)) {
    return { intent: 'promenade', professionApiSlug: 'dog-walker', seoMetierSlug: 'promeneur-canin' };
  }

  return { intent: 'generic', professionApiSlug: 'trainers', seoMetierSlug: 'educateur-canin' };
}

const FOLLOWUP_COMPORTEMENT_TOKENS = [
  ...COMPORTEMENT_TOKENS,
  'comportementaliste',
  'aboie',
  'aboiement',
  'aboiements',
  'agressif',
  'agressive',
  'detruit',
  'destruction',
];
const FOLLOWUP_EDUC_TOKENS = [...EDUC_TOKENS, 'marche'];

/** Au 2e message, « il a peur » gagne sur le « laisse » du premier. */
export function classifyConversation(
  messages: { role: string; content: string }[]
): ClassifiedAsk {
  const users = messages.filter(m => m.role === 'user' && m.content.trim());
  const phrase = users.map(m => m.content).join(' ');
  if (users.length <= 1) return classifyAskIntent(phrase);

  const last = normalizeAskText(users[users.length - 1].content);
  if (hasToken(last, FOLLOWUP_COMPORTEMENT_TOKENS)) {
    return {
      intent: 'comportement',
      professionApiSlug: 'behaviorist',
      seoMetierSlug: 'comportementaliste-canin',
    };
  }
  if (hasToken(last, FOLLOWUP_EDUC_TOKENS)) {
    return { intent: 'educ', professionApiSlug: 'trainers', seoMetierSlug: 'educateur-canin' };
  }

  const lastClass = classifyAskIntent(users[users.length - 1].content);
  if (lastClass.intent !== 'generic') return lastClass;
  return classifyAskIntent(phrase);
}

export function askHasProblemTokens(phrase: string): boolean {
  const classified = classifyAskIntent(phrase);
  return classified.intent !== 'generic';
}
